/**
 * GroupDiningCreatePage — Iter 195.
 *
 * The host (a logged-in Hustle member) creates a group dinner: picks a
 * restaurant + date + time + party size, then adds invitees by name /
 * email / phone. Existing members are matched automatically by their
 * contact details; non-members receive an invite link with iOS +
 * Android app-store links baked in — when they sign up the host earns
 * $5 store credit as referral.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function GroupDiningCreatePage() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [bizId, setBizId] = useState("");
  const [when, setWhen] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(19, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [partySize, setPartySize] = useState(4);
  const [notes, setNotes] = useState("");
  const [invitees, setInvitees] = useState([]);
  const [locationOptIn, setLocationOptIn] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", email: "", phone: "" });

  useEffect(() => {
    api
      .get("/businesses")
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : data?.businesses || [];
        setBusinesses(rows);
        if (rows[0]) setBizId(rows[0].id);
      })
      .catch(() => {});
  }, []);

  const addInvitee = () => {
    const clean = {
      name: draft.name.trim(),
      email: draft.email.trim().toLowerCase() || null,
      phone: draft.phone.trim() || null,
    };
    if (!clean.name && !clean.email && !clean.phone) {
      toast.error("Give the invitee a name, email or phone.");
      return;
    }
    setInvitees((prev) => [...prev, clean]);
    setDraft({ name: "", email: "", phone: "" });
  };

  const removeInvitee = (idx) => {
    setInvitees((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!bizId) return toast.error("Pick a restaurant.");
    setCreating(true);
    try {
      const bookingIso = new Date(when).toISOString();
      const { data } = await api.post("/group-dining", {
        business_id: bizId,
        booking_time_iso: bookingIso,
        party_size: Number(partySize),
        notes: notes || null,
        invitees,
        location_opt_in_default: locationOptIn,
      });
      toast.success("Group dinner created — invitations sent.");
      navigate(`/group-dining/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      data-testid="group-dining-create-page"
      className="min-h-screen bg-slate-50 py-10 px-4"
    >
      <div className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-900 mb-2">
          Plan a group dinner
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          Pick the restaurant, invite the crew — everyone can order their
          own dishes, and closer to booking time you&apos;ll see who is
          on their way on a live map.
        </p>

        {/* Restaurant */}
        <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">
          Restaurant
        </label>
        <select
          data-testid="grp-biz-select"
          value={bizId}
          onChange={(e) => setBizId(e.target.value)}
          className="w-full mb-4 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
        >
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name || b.slug}
            </option>
          ))}
        </select>

        {/* When + party size */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">
              When
            </label>
            <input
              data-testid="grp-when"
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">
              Party size
            </label>
            <input
              data-testid="grp-party-size"
              type="number"
              min={1}
              max={40}
              value={partySize}
              onChange={(e) => setPartySize(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/* Notes */}
        <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">
          Notes for the restaurant (optional)
        </label>
        <textarea
          data-testid="grp-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="birthday, wheelchair access, dietary flags…"
          className="w-full mb-6 border border-slate-200 rounded-lg px-3 py-2 text-sm"
        />

        {/* Invitees */}
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-2">
            Invite people
          </h2>
          <div className="grid grid-cols-3 gap-2 mb-2">
            <input
              data-testid="grp-invitee-name"
              placeholder="Name"
              value={draft.name}
              onChange={(e) =>
                setDraft({ ...draft, name: e.target.value })
              }
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
            <input
              data-testid="grp-invitee-email"
              placeholder="Email"
              value={draft.email}
              onChange={(e) =>
                setDraft({ ...draft, email: e.target.value })
              }
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
            <input
              data-testid="grp-invitee-phone"
              placeholder="Phone (+61…)"
              value={draft.phone}
              onChange={(e) =>
                setDraft({ ...draft, phone: e.target.value })
              }
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <button
            data-testid="grp-invitee-add-btn"
            onClick={addInvitee}
            className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50"
          >
            <Icons.Plus size={13} className="inline -mt-0.5" /> Add invitee
          </button>
        </div>

        {invitees.length > 0 && (
          <ul
            data-testid="grp-invitee-list"
            className="mb-6 divide-y divide-slate-100 border border-slate-100 rounded-lg"
          >
            {invitees.map((iv, i) => (
              <li
                key={i}
                className="flex items-center justify-between px-3 py-2 text-sm"
              >
                <span>
                  <strong>{iv.name || "Guest"}</strong>
                  <span className="ml-2 text-slate-500">
                    {iv.email || iv.phone || ""}
                  </span>
                </span>
                <button
                  data-testid={`grp-invitee-remove-${i}`}
                  onClick={() => removeInvitee(i)}
                  className="text-rose-600 text-xs hover:underline"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Location opt-in default */}
        <label className="flex items-start gap-2 mb-6 text-sm text-slate-700">
          <input
            data-testid="grp-location-optin"
            type="checkbox"
            checked={locationOptIn}
            onChange={(e) => setLocationOptIn(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Invitees can share live location from 30 min before the
            booking so we can see who&apos;s on the way (opt-in per
            person — anyone can turn it off).
          </span>
        </label>

        {/* Referral hint */}
        <div className="mb-6 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          🎁 Non-members who join from your invite get you $5 store
          credit each — automatically.
        </div>

        <button
          data-testid="grp-submit-btn"
          onClick={submit}
          disabled={creating}
          className="w-full px-4 py-3 rounded-lg bg-slate-900 text-white font-semibold disabled:opacity-50"
        >
          {creating ? "Sending invites…" : "Create + send invites"}
        </button>
      </div>
    </div>
  );
}
